import {
  Avatar,
  FlexBox,
  InputSelectionListProps,
  Text,
} from '@dixa/lib-shared-habla';
import type { SearchUser } from 'lib-search-types';
import { HookFormFieldWrappedBaseProps } from 'lib-shared-hook-form';
import { FieldPath, FieldValues } from 'react-hook-form';

import {
  HookFormSearchSelect,
  HookFormSearchSelectProps,
} from 'components/hookForm/hookFormSearchSelect';
import { getUserDisplayName } from 'utils/user';

import styles from './hookFormUserSelect.module.scss';

type HookFormUserSelectProps = Partial<
  Omit<InputSelectionListProps<SearchUser>, 'children'>
>;

/**
 * A wrapper around {@link HookFormSearchSelect} for searching and selecting
 * users. Provides user-specific rendering (avatar, name, email/phone) and
 * sensible defaults. Consumers control which users are shown via `initialArg`
 * filters (e.g. user type).
 */
export const HookFormUserSelect = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  componentProps = {},
  initialArg,
  lazy,
  getSuggestions,
  transformValue,
  ...listProps
}: HookFormFieldWrappedBaseProps<TFieldValues, TName, HookFormUserSelectProps> &
  Pick<
    HookFormSearchSelectProps<'users', TFieldValues, TName>,
    'initialArg' | 'lazy' | 'getSuggestions' | 'transformValue'
  >): JSX.Element => {
  return (
    <HookFormSearchSelect<'users', TFieldValues, TName>
      {...listProps}
      scope="users"
      initialArg={initialArg}
      lazy={lazy}
      getSuggestions={getSuggestions}
      transformValue={transformValue}
      renderSelectionListItemContent={(user) => {
        const name = getUserDisplayName(user);
        const secondLine = user.email ?? user.phone_number;
        const isNameAndSecondLineEqual = name === secondLine;

        return (
          <FlexBox
            gap="200"
            alignItems="center"
            className={styles.selectionListItemContent}
          >
            <Avatar
              user={{
                name: user.name,
                avatar_url: user.avatar_url,
                phone_number: user.phone_number,
                email: user.email,
              }}
            />

            <div>
              <Text.Heading variant="heading-4">{name}</Text.Heading>

              {!isNameAndSecondLineEqual && (
                <Text.Paragraph as="div" variant="4" subdued>
                  <FlexBox gap="200" alignItems="center">
                    {user.phone_number && <span>{user.phone_number}</span>}
                    {user.email && <span>{user.email}</span>}
                  </FlexBox>
                </Text.Paragraph>
              )}
            </div>
          </FlexBox>
        );
      }}
      componentProps={{
        placeholder: 'Select a user',
        equalityFn: (a: SearchUser, b: SearchUser) => a.id === b.id,
        getItemTitleByValue: (user: SearchUser | null | undefined) =>
          user ? getUserDisplayName(user) : '',
        ...componentProps,
      }}
    />
  );
};
